import { MusicAccess } from "../../../types";
import { useMusicPreviewPlayer } from "./useMusicPreviewPlayer";
import { memo, useEffect, useCallback } from "react";
import { PREVIEW_DURATION_IN_SECONDS } from "../../../config";
import { useProgressStyle } from "./useProgressStyle";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LIcon from "@components/LIcon";
import Button from "../../common/Button";
import { theme } from "@theme";
import { useIsPlaying, usePlayerControls } from "@utils/player";

const Preview = memo(({ song }: { song: MusicAccess["songs"][number] }) => {
  const player = useMusicPreviewPlayer({
    url: song.previewUrl ?? "",
  });

  const isMainPlaying = useIsPlaying();
  const { pause: pauseMain } = usePlayerControls();

  // Auto-pause and reset when preview duration is reached
  useEffect(() => {
    if (player.playing && player.currentTime >= PREVIEW_DURATION_IN_SECONDS) {
      player.pause();
      player.seekTo(0).catch((err) => {
        console.error(":: MusicPreview :: error seeking to 0", err);
      });
    }
  }, [player]);

  useEffect(() => {
    if (isMainPlaying && player.currentTime !== 0) {
      player.pause();
    }
  }, [isMainPlaying, player.currentTime]);

  const animatedProgressStyle = useProgressStyle({
    playing: player.playing,
    currentTime: player.currentTime,
    previewDurationInSeconds: PREVIEW_DURATION_IN_SECONDS,
  });

  const playPausePreview = useCallback(() => {
    if (isMainPlaying) pauseMain().catch(console.error);

    if (player.playing) {
      player.pause();
    } else {
      player.play();
    }
  }, [isMainPlaying, pauseMain, player]);

  return (
    <LBox borderRadius="m-l" overflow="hidden">
      <Button.Button
        onPress={playPausePreview}
        sentryEventName="music_preview_play_pause"
        bg="whiteTranslucent20"
      >
        <LIcon
          colors={{ color: theme.colors.lightGrey }}
          name={player.playing ? "pause" : "play"}
          size={16}
        />
        <LText variant="Caption/C1/Medium" color="white">
          Preview
        </LText>
      </Button.Button>
      <LBox
        position="absolute"
        left={0}
        right={0}
        bottom={0}
        height={3}
        backgroundColor="white"
      >
        <LBox
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="accessPreviewProgressBar"
          style={animatedProgressStyle}
        />
      </LBox>
    </LBox>
  );
});
Preview.displayName = "Preview";

// Wrapper component with cleanup effect to ensure preview stops on unmount
const PreviewWrapper = ({ song }: { song?: MusicAccess["songs"][number] }) => {
  if (!song) return null;
  return <Preview song={song} />;
};
PreviewWrapper.displayName = "PreviewWrapper";

export default PreviewWrapper;
